// apps/app/src/audio/biomeLookup.ts
//
// Spatial lookups for the audio director: given a camera world position,
// find the biome of the nearest tile and detect whether the camera is
// hovering over a city. Used to pick the appropriate ambient bed.

import type { GameSnapshot } from '@aod/protocol';

/**
 * Sample the biome of the tile nearest to the given world (x, z) position.
 * Returns null if the snapshot is null or no tile nodes exist.
 *
 * Strategy: linear scan, pick the tile with the smallest squared distance
 * to the camera's (x, z). Squared distance avoids a sqrt per comparison.
 * One-hex slop near vertices is acceptable for ambient audio.
 */
export function sampleBiomeAtWorld(
  snapshot: GameSnapshot | null,
  worldPos: readonly [number, number, number],
): string | null {
  if (!snapshot) return null;
  const children = snapshot.scene.children ?? [];
  let nearest: string | null = null;
  let bestDist = Number.POSITIVE_INFINITY;
  for (const node of children) {
    if (node.type !== 'tile') continue;
    const [tx, , tz] = node.transform.position;
    const dx = tx - worldPos[0];
    const dz = tz - worldPos[2];
    const d = dx * dx + dz * dz;
    if (d < bestDist) {
      const props = node.props as Record<string, unknown> | undefined;
      const biome = props?.biome;
      if (typeof biome === 'string') {
        // Only advance bestDist when we actually accept this tile,
        // so a closer tile with a malformed biome doesn't suppress a
        // farther valid one.
        bestDist = d;
        nearest = biome;
      }
    }
  }
  return nearest;
}

/**
 * Return true if any city node in the snapshot is within `threshold` world
 * units of the camera's (x, z) position. Default threshold 1.5 ≈ 1.5 hexes.
 */
export function isCameraOverCity(
  snapshot: GameSnapshot | null,
  worldPos: readonly [number, number, number],
  threshold = 1.5,
): boolean {
  if (!snapshot) return false;
  const children = snapshot.scene.children ?? [];
  const thresholdSq = threshold * threshold;
  for (const node of children) {
    if (node.type !== 'city') continue;
    const [cx, , cz] = node.transform.position;
    const dx = cx - worldPos[0];
    const dz = cz - worldPos[2];
    const d = dx * dx + dz * dz;
    if (d <= thresholdSq) return true;
  }
  return false;
}
